import z from "zod";

import {
  createMetadataFieldValueSchema,
  MetadataFieldTypes,
  MetadataValueSchemaOutput,
} from "./campaignMetadata";

export const DatabaseModeEnum = z.enum(["MY_CAMPAIGN", "MY_VOTERS"]);

export const IntegrationSettingsSchema = z.object({
  databaseMode: DatabaseModeEnum,
});

export const EditBasicsInputSchema = z.object({
  name: z.string({ message: "Required" }).min(3, "Too short"),
  description: z.string(),
  inboxId: z.string().uuid().nullable(),
  integrationId: z.string().uuid().optional(),
  integrationSettings: IntegrationSettingsSchema.optional(),
  textingHoursTimezone: z.string(),
  textingHoursStart: z.string().time({ precision: 0 }).optional(),
  textingHoursEnd: z.string().time({ precision: 0 }).optional(),
  texterAssignmentBatchSize: z.coerce.number(),
  texterAssignmentExpirationMinutes: z.coerce.number(),
});

export const MetadataFormSchemaBase = z.object({
  name: z.string(),
  fieldId: z.string().uuid(),
  id: z.string().uuid().nullable(),
  type: z.nativeEnum(MetadataFieldTypes),
});

export const EditBasicUISchema = z
  .object({
    ["_submitError"]: z.string().nullish(),
    inboxOverride: z.boolean(),
    integrationIdAndDbMode: z.string(),
    textingHoursOverrideSubaccount: z.boolean(),
    texterAssignmentBatchSizeOverrideSubaccount: z.boolean(),
    texterAssignmentExpirationMinutesOverrideSubaccount: z.boolean(),
    goToNext: z.boolean().nullish(),
  })
  .merge(EditBasicsInputSchema);

export type BasicFormInput = z.input<typeof EditBasicUISchema>;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createSchemaForForm(
  fields: Record<string, MetadataValueSchemaOutput>
) {
  const metadataFieldsSchema = Object.fromEntries(
    Object.entries(fields).map(([name, field]) => {
      const valueSchema = createMetadataFieldValueSchema(field);
      const metadataFieldSchema = z
        .object({
          value: valueSchema,
        })
        .merge(MetadataFormSchemaBase);
      return [name, metadataFieldSchema];
    })
  );

  return EditBasicUISchema.merge(
    z.object({
      metadata: z.object(metadataFieldsSchema),
    })
  ).superRefine((data, ctx) => {
    if (data.textingHoursOverrideSubaccount) {
      if (!data.textingHoursTimezone) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["textingHoursTimezone"],
        });
      }
      if (!data.textingHoursStart) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["textingHoursStart"],
        });
      }
      if (!data.textingHoursEnd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["textingHoursEnd"],
        });
      }

      if (
        data.textingHoursStart &&
        data.textingHoursEnd &&
        data.textingHoursStart >= data.textingHoursEnd
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be after start time",
          path: ["textingHoursEnd"],
        });
      }
    }

    if (data.texterAssignmentBatchSizeOverrideSubaccount) {
      if (data.texterAssignmentBatchSize < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be at least 1",
          path: ["texterAssignmentBatchSize"],
        });
      }
      if (data.texterAssignmentBatchSize > 250) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be at most 250",
          path: ["texterAssignmentBatchSize"],
        });
      }
    }

    if (data.texterAssignmentExpirationMinutesOverrideSubaccount) {
      if (data.texterAssignmentExpirationMinutes < 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be at least 5",
          path: ["texterAssignmentExpirationMinutes"],
        });
      }

      if (data.texterAssignmentExpirationMinutes > 24 * 60) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be at most 24 hours",
          path: ["texterAssignmentExpirationMinutes"],
        });
      }
    }

    if (data.inboxOverride && !data.inboxId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Inbox selection is required when override is enabled",
        path: ["inboxOverride"],
      });
    }
  });
}
